import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Clear as ClearIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import {
  exercisesApi,
  isPremiumExerciseType,
  type ExerciseLevel,
  type ExerciseStatus,
  type ExerciseType,
} from '@/api/exercises'
import { useSubscriptionParameterFresh } from '@/hooks/useSubscriptionParameterFresh'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/utils/error'

/** Размер страницы банка: диалог узкий, длинная простыня в нём не читается */
const BANK_PER_PAGE = 20

/** Пауза перед запросом по строке поиска — иначе запрос уходит на каждую букву */
const SEARCH_DEBOUNCE_MS = 350

/**
 * Высота области списка фиксирована: скелетон, список и пустое состояние
 * занимают один и тот же прямоугольник, поэтому диалог не прыгает при каждой
 * перезагрузке выдачи (поиск, «показать ещё», смена урока).
 */
const LIST_BOX_HEIGHT = 'min(55vh, 420px)'

/** Почему задание нельзя взять в домашку — от этого зависит текст подсказки */
type LockReason = 'system' | 'premium'

/**
 * Задание в пикере — усечённый ресурс, который отдаёт сервер.
 *
 * Ни `content`, ни `settings` здесь нет и быть не должно: пикер только
 * называет задание, а тянуть в список содержимое восемнадцати типов — это
 * лишние килобайты на каждую страницу банка.
 *
 * `assigned_element_id` — слот выдачи, место задания в программе ученика.
 * Одного `id` задания для домашки мало: то же задание банка стоит в двух
 * темах и приходит из секции `program` двумя строками, и только элемент темы
 * говорит, в чью статистику пойдёт работа. У строк банка слота нет — `null`.
 */
export type PickerExercise = {
  id: number
  type: ExerciseType
  level: ExerciseLevel
  title: string
  status: ExerciseStatus
  is_system: boolean
  assigned_element_id: number | null
}

/** Группа заданий одной темы программы ученика */
type PickerGroup = {
  assigned_theme_id: number
  title: string | null
  section_title: string | null
  pinned: boolean
  exercises: PickerExercise[]
}

/**
 * Приведение ответа к тому, что рисует диалог.
 *
 * Параметр описан минимальным набором полей: так пикер переживёт и усечение
 * ресурса на сервере, и обратное расширение — читаем мы всё равно только эти
 * семь полей.
 *
 * Слот приводим к `null` явно: в секции банка сервер поля не шлёт вовсе, и
 * `undefined` в теле запроса молча исчез бы при сериализации — сервер не
 * отличил бы «слота нет» от «клиент старой версии».
 */
function toPickerExercise(raw: {
  id: number
  type: ExerciseType
  level: ExerciseLevel
  title: string
  status: ExerciseStatus
  is_system: boolean
  assigned_element_id?: number | null
}): PickerExercise {
  return {
    id: raw.id,
    type: raw.type,
    level: raw.level,
    title: raw.title,
    status: raw.status,
    is_system: raw.is_system,
    assigned_element_id: raw.assigned_element_id ?? null,
  }
}

/**
 * Выбор задания для пункта домашки: программа ученика + весь банк учителя.
 *
 * Сверху идут задания тем программы этого ученика — они попадают в статистику
 * темы, поэтому у каждой группы бейдж «По программе обучения». Ниже — банк
 * заданий (свои и витрина платформы): раньше их выбрать было нечем, и учитель
 * упирался в один-два пункта программы. Задание из банка выдать можно, но в
 * статистику темы оно не пойдёт — об этом сказано подписью у раздела.
 *
 * Черновики и закрытые подпиской задания отключены: выдать ученику
 * недописанное задание значит отправить его решать полупустую форму, а
 * премиум-тип или витрину сервер всё равно не пустит в работу.
 */
export function ExercisePickerDialog({
  open,
  lessonId,
  onClose,
  onPick,
}: {
  open: boolean
  lessonId: number
  onClose: () => void
  onPick: (exercise: PickerExercise) => void
}) {
  const { t } = useTranslation()

  // Гейты подписки читаем и на клиенте: сервер закрывает вход в задание сам,
  // но кликабельная строка, падающая с 403, выглядит поломкой домашки.
  // Обновление карты параметров дёргаем только с открытым диалогом — второй
  // флаг берём из уже обновлённого стора, чтобы не слать тот же GET дважды.
  //
  // Админа гейты не касаются: подписки у него нет, а сервер (ExerciseAccessGate)
  // его из-под них выводит явно — без этой ветки админ видел бы замок на
  // контенте платформы, который сам же и ведёт.
  const isAdmin = useAuthStore(
    (state) => state.user?.roles?.some((role) => role.slug === 'admin') ?? false,
  )
  const premiumEnabled =
    useSubscriptionParameterFresh('premium_exercises_enabled', open && !isAdmin) || isAdmin
  const systemProgramsEnabled =
    useAuthStore(
      (state) => state.user?.subscription_parameters?.system_programs_enabled === 'true',
    ) || isAdmin

  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [program, setProgram] = useState<PickerGroup[]>([])
  const [bankItems, setBankItems] = useState<PickerExercise[]>([])
  const [bankTotal, setBankTotal] = useState(0)
  const [bankPage, setBankPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Первая загрузка рисует скелетон, повторные — только затемняют список:
  // иначе диалог схлопывается на каждую букву поиска
  const loadedOnce = useRef(false)
  // Номер запроса общий для первой страницы и для «показать ещё»: без него
  // страница старого поиска дописывалась к результатам нового
  const requestRef = useRef(0)

  useEffect(() => {
    if (!open) return

    const debounce = window.setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(debounce)
  }, [query, open])

  useEffect(() => {
    if (!open) {
      // Сброс при закрытии: иначе следующий вызов на другом уроке успевает
      // показать прежние темы и прежний поиск до ответа сервера
      requestRef.current += 1
      setQuery('')
      setSearch('')
      setProgram([])
      setBankItems([])
      setBankTotal(0)
      setBankPage(1)
      setError(null)
      setLoadingMore(false)
      loadedOnce.current = false

      return
    }

    const request = ++requestRef.current
    const first = !loadedOnce.current

    if (first) setLoading(true)
    else setSearching(true)
    setError(null)

    exercisesApi
      .listForHomework(lessonId, { q: search || undefined, page: 1, perPage: BANK_PER_PAGE })
      .then((data) => {
        // Ответ на прежнюю строку поиска приходит после нового запроса и
        // затирает свежий список, если его не отбросить
        if (request !== requestRef.current) return

        setProgram(
          data.program.map((group) => ({
            assigned_theme_id: group.assigned_theme_id,
            title: group.title,
            section_title: group.section_title,
            pinned: group.pinned,
            exercises: group.exercises.map(toPickerExercise),
          })),
        )
        setBankItems(data.bank.items.map(toPickerExercise))
        setBankTotal(data.bank.total)
        setBankPage(data.bank.page)
        loadedOnce.current = true
      })
      .catch((err: unknown) => {
        if (request === requestRef.current) setError(getErrorMessage(err) || t('common.error'))
      })
      .finally(() => {
        if (request !== requestRef.current) return

        setLoading(false)
        setSearching(false)
      })
  }, [open, lessonId, search, t])

  const loadMore = useCallback(async () => {
    const request = ++requestRef.current

    setLoadingMore(true)
    setError(null)
    try {
      const data = await exercisesApi.listForHomework(lessonId, {
        q: search || undefined,
        page: bankPage + 1,
        perPage: BANK_PER_PAGE,
      })

      // Пока грузилась страница, учитель мог набрать другой запрос: дописывать
      // её в новый список нельзя — там уже другая выдача
      if (request !== requestRef.current) return

      const page = data.bank.items.map(toPickerExercise)

      setBankItems((prev) => {
        // Между страницами задание могли добавить, и оно сдвинет выдачу: без
        // отсева пришедшая копия даст два элемента с одним key
        const known = new Set(prev.map((each) => each.id))

        return [...prev, ...page.filter((each) => !known.has(each.id))]
      })
      setBankTotal(data.bank.total)
      setBankPage(data.bank.page)
    } catch (err) {
      if (request === requestRef.current) setError(getErrorMessage(err) || t('common.error'))
    } finally {
      if (request === requestRef.current) setLoadingMore(false)
    }
  }, [lessonId, search, bankPage, t])

  const pick = (exercise: PickerExercise) => {
    onPick(exercise)
    onClose()
  }

  /**
   * Замок задания — целиком клиентский: сервер недоступное не отдаёт, он его
   * просто вырезает из выдачи, а в ресурсе пикера нет ни одного поля про
   * доступность (только id, type, level, title, status, is_system). Читать
   * «замок с сервера» было нечего.
   *
   * `placed` — задание уже стоит в программе ученика (секция «Из программы»).
   * Такие сервер намеренно оставляет в выдаче даже без `system_programs_enabled`
   * (мягкая деградация: даунгрейд не должен ломать курс посреди прохождения),
   * и клиентский замок на них жёстче сервера — учитель видел задание, которое
   * ученик уже проходит, и не мог выдать его в домашку.
   *
   * На премиум-тип поблажка не распространяется — сервер её там тоже не даёт:
   * премиум это функция тарифа, а не единица уже выданного контента.
   */
  const lockOf = (exercise: PickerExercise, placed: boolean): LockReason | null => {
    if (exercise.is_system && !systemProgramsEnabled && !placed) return 'system'
    if (isPremiumExerciseType(exercise.type) && !premiumEnabled) return 'premium'

    return null
  }

  const lockHint = (reason: LockReason): string => {
    if (reason === 'system') {
      return t(
        'homeworks.exercisePicker.lockedSystem',
        'Готовые задания платформы доступны на расширенной подписке',
      )
    }
    return t(
      'homeworks.exercisePicker.lockedPremium',
      'Этот тип задания доступен на премиум-подписке',
    )
  }

  const pickable = (exercise: PickerExercise, placed: boolean) =>
    exercise.status === 'ready' && !lockOf(exercise, placed)

  const programExercises = program.flatMap((group) => group.exercises)
  // Задания этой секции пришли из тем программы ученика — они «размещённые»
  const programHasPickable = programExercises.some((each) => pickable(each, true))
  const programOnlyDrafts =
    programExercises.length > 0 && programExercises.every((each) => each.status !== 'ready')
  const hasMoreBank = bankItems.length < bankTotal

  /**
   * Ключ строки списка.
   *
   * По `exercise.id` строки не различаются: одно задание банка можно поставить
   * в две темы программы, и сервер клонирует его на каждый элемент темы — в
   * секции «Из программы» приходят две строки с одним `id`. Одинаковый key в
   * одном списке ломает реконсиляцию при перерисовке (поиск, догрузка банка):
   * замок и обработчик выбора могут приехать в чужую строку, и учитель выдаст
   * слот другого элемента, чем нажал. Различает строки именно слот выдачи.
   *
   * У банка слота нет — там ключ по `id` (дубликаты отсеиваются при догрузке),
   * а префиксы разводят два пространства ключей, чтобы они не столкнулись.
   */
  const rowKeyOf = (exercise: PickerExercise) =>
    exercise.assigned_element_id !== null
      ? `element-${exercise.assigned_element_id}`
      : `bank-${exercise.id}`

  const renderExercise = (exercise: PickerExercise, placed: boolean) => {
    const draft = exercise.status !== 'ready'
    const lock = lockOf(exercise, placed)
    const disabled = draft || lock !== null
    const rowKey = rowKeyOf(exercise)

    const row = (
      <ListItemButton key={rowKey} disabled={disabled} onClick={() => pick(exercise)}>
        {/* disableTypography: во второй строке живёт Stack, а обёртка ListItemText
            по умолчанию кладёт её в <p> — вложенный div в абзаце ломает разметку */}
        <ListItemText
          disableTypography
          primary={
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Название — единственный элемент первой строки: раньше оно тонуло
                  среди чипов того же кегля и список читался как россыпь ярлыков */}
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                {exercise.title}
              </Typography>
              {/* Справа — только то, что меняет кликабельность строки */}
              {draft && (
                <Chip size="small" color="warning" label={t('exercises.exerciseStatus.draft')} />
              )}
              {/* Замок виден и без наведения: подсказка на тачскрине не открывается */}
              {!draft && lock !== null && (
                <Chip
                  size="small"
                  color="default"
                  variant="outlined"
                  icon={<LockIcon fontSize="small" />}
                  label={t('homeworks.exercisePicker.lockedBadge', 'Недоступно')}
                />
              )}
            </Stack>
          }
          secondary={
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.25 }}
            >
              <Typography variant="caption" color="text.secondary">
                {t(`exercises.types.${exercise.type}.name`)}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                ·
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(`exercises.levels.${exercise.level}`)}
              </Typography>
              {exercise.is_system && (
                <>
                  <Typography variant="caption" color="text.disabled">
                    ·
                  </Typography>
                  <PublicIcon sx={{ fontSize: 14 }} color="disabled" />
                  <Typography variant="caption" color="text.secondary">
                    {t('homeworks.exercisePicker.systemBadge', 'Платформа')}
                  </Typography>
                </>
              )}
              {/* Причина замка текстом: серая строка без объяснения читается как
                  поломка списка, а не как ограничение подписки */}
              {!draft && lock !== null && (
                <>
                  <Typography variant="caption" color="text.disabled">
                    ·
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lockHint(lock)}
                  </Typography>
                </>
              )}
            </Stack>
          }
        />
      </ListItemButton>
    )

    if (!disabled) return row

    return (
      <Tooltip
        key={rowKey}
        title={
          draft
            ? t(
                'homeworks.exercisePicker.draftHint',
                'Черновик выдать нельзя — задание ещё не дописано',
              )
            : lockHint(lock as LockReason)
        }
      >
        <Box component="span" sx={{ display: 'block' }}>
          {row}
        </Box>
      </Tooltip>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('homeworks.exercisePicker.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('homeworks.exercisePicker.searchPlaceholder', 'Поиск по названию')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={t('homeworks.exercisePicker.clearSearch', 'Очистить поиск')}
                      onClick={() => setQuery('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          {error && <Alert severity="error">{error}</Alert>}

          {/* Область списка фиксированной высоты: диалог не меняет размер ни
              при поиске, ни при догрузке страницы банка */}
          <Box sx={{ height: LIST_BOX_HEIGHT, overflowY: 'auto', pr: 0.5 }}>
            {loading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={28} width="50%" />
                <Skeleton variant="rounded" height={44} />
                <Skeleton variant="rounded" height={44} />
                <Skeleton variant="rounded" height={28} width="40%" />
                <Skeleton variant="rounded" height={44} />
                <Skeleton variant="rounded" height={44} />
              </Stack>
            ) : (
              <Box
                sx={{
                  // Список остаётся на месте, пока идёт поиск: подменять его
                  // скелетоном значит терять уже прочитанное учителем
                  opacity: searching ? 0.5 : 1,
                  pointerEvents: searching ? 'none' : 'auto',
                  transition: 'opacity 120ms',
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2">
                        {t('homeworks.exercisePicker.programSection', 'Из программы ученика')}
                      </Typography>
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={t('homeworks.exercisePicker.programBadge', 'По программе обучения')}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        'homeworks.exercisePicker.programHint',
                        'Такое задание засчитывается в статистику своей темы',
                      )}
                    </Typography>

                    {program.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {search
                          ? t(
                              'homeworks.exercisePicker.emptyProgramSearch',
                              'В программе ученика по запросу ничего нет',
                            )
                          : t(
                              'homeworks.exercisePicker.emptyProgram',
                              'В программе ученика заданий нет',
                            )}
                      </Typography>
                    )}

                    {/* Список из одних серых строк без объяснения выглядит поломкой */}
                    {program.length > 0 && !programHasPickable && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {programOnlyDrafts
                          ? t('homeworks.exercisePicker.onlyDrafts')
                          : t(
                              'homeworks.exercisePicker.programNonePickable',
                              'Задания тем сейчас выдать нельзя: они либо черновики, либо закрыты подпиской',
                            )}
                      </Alert>
                    )}
                  </Box>

                  {program.map((group) => (
                    <List
                      key={group.assigned_theme_id}
                      dense
                      disablePadding
                      subheader={
                        <ListSubheader disableSticky sx={{ px: 0, lineHeight: 2 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <span>{group.title ?? t('programs.untitledTheme')}</span>
                            {group.section_title && (
                              <Typography variant="caption" color="text.secondary">
                                {group.section_title}
                              </Typography>
                            )}
                            {/* Бейдж «По программе обучения» стоит один раз — в
                                заголовке секции: у каждой темы он лишний шум */}
                            {group.pinned && (
                              <Chip
                                size="small"
                                color="primary"
                                label={t('homeworks.exercisePicker.lessonTheme')}
                              />
                            )}
                          </Stack>
                        </ListSubheader>
                      }
                    >
                      {group.exercises.map((exercise) => renderExercise(exercise, true))}
                    </List>
                  ))}

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2">
                      {t('homeworks.exercisePicker.bankSection', 'Из банка заданий')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        'homeworks.exercisePicker.bankHint',
                        'Задание вне программы ученика — в статистику темы оно не войдёт',
                      )}
                    </Typography>

                    {bankItems.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {search
                          ? t(
                              'homeworks.exercisePicker.emptyBankSearch',
                              'В банке заданий по запросу ничего не найдено',
                            )
                          : t('homeworks.exercisePicker.emptyBank', 'В банке заданий пока пусто')}
                      </Typography>
                    ) : (
                      <List dense disablePadding sx={{ mt: 0.5 }}>
                        {/* Банк — это как раз «взять новое»: поблажки для витрины здесь нет,
                            сервер её из выдачи банка тоже убирает (scopeUsable без withPlaced) */}
                        {bankItems.map((exercise) => renderExercise(exercise, false))}
                      </List>
                    )}

                    {bankTotal > 0 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mt: 1, flexWrap: 'wrap' }}
                        useFlexGap
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('homeworks.exercisePicker.shownOf', 'Показано {{shown}} из {{total}}', {
                            shown: bankItems.length,
                            total: bankTotal,
                          })}
                        </Typography>
                        {hasMoreBank && (
                          <Button
                            size="small"
                            disabled={loadingMore}
                            startIcon={loadingMore ? <CircularProgress size={16} /> : undefined}
                            onClick={() => {
                              void loadMore()
                            }}
                          >
                            {t('homeworks.exercisePicker.showMore', 'Показать ещё')}
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  )
}
